import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import invariant from 'tiny-invariant';

import { SigmaModelParameterForm } from '#screens/sigma-model-parameter-form/sigma-model-parameter-form.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/parameters/',
)({
	staticData: {
		navigation: {
			label: 'Package: Parameters',
			keywords: ['params'],
		},
	},
	beforeLoad: ({ context: { trpc } }) => ({
		getSigmaModelDefinitionsOptions: trpc.misc.getSigmaModelDefinitions.queryOptions({}, { staleTime: Infinity }),
	}),
	loader: ({ context: { queryClient, getPackageOptions, getSigmaModelDefinitionsOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		void queryClient.prefetchQuery(getSigmaModelDefinitionsOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
					'Parameters • Package Editor • Cadstrom',
			},
		],
	}),
	component: ParametersTab,
});

function ParametersTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions, getSigmaModelDefinitionsOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);
	const {
		data: { sigmaModelDefinitions },
	} = useSuspenseQuery(getSigmaModelDefinitionsOptions);
	const sigmaModelDefinition = sigmaModelDefinitions.find((model) => model.name === pkg.sigmaModelType);
	invariant(sigmaModelDefinition, 'Sigma Model definition not found. Change the Sigma Model type in the General tab.');

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				setServerParameters(res.pkg.sigmaModelParameters);
			},
		}),
	);

	const {
		draftState: draftParameters,
		setDraftState: setDraftParameters,
		setServerState: setServerParameters,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: pkg.sigmaModelParameters,
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (draftParameters) =>
			await updatePackage({
				id: pkg.id,
				data: {
					sigmaModelParameters: { set: draftParameters },
				},
			}),
	});

	return (
		<div ref={saveAreaContainerRef} className='relative flex min-h-0 grow basis-0 flex-col overflow-auto px-3 py-2'>
			<SigmaModelParameterForm
				key={pkg.id} // Reinitialize form when navigating between packages
				sigmaModelDefinition={sigmaModelDefinition}
				parameters={draftParameters}
				onFieldChange={(parameterName, value) =>
					setDraftParameters((prev) => ({
						...prev,
						[parameterName]: {
							value: value.value === undefined ? (prev[parameterName]?.value ?? null) : value.value,
							variance: value.variance === undefined ? (prev[parameterName]?.variance ?? null) : value.variance,
						},
					}))
				}
				onReplace={(parameters) => setDraftParameters(parameters)}
			/>

			<Button
				disabled={!isDirty}
				loading={updatePackageIsPending}
				startIcon={<SaveIcon />}
				className='absolute top-2 right-3'
				onClick={() => void handleSave()}
			>
				<p>Save</p>
			</Button>
		</div>
	);
}
